import React, {
  useState,
  useMemo,
  useContext,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  Grid,
  alpha,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  Drawer,
  Alert,
  useTheme,
  Chip,
  InputAdornment,
  CircularProgress,
  Backdrop,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  AccountBalanceWallet,
  AttachMoney,
  AccountCircle,
  Event,
  Group,
} from "@mui/icons-material";
import { format, parse } from "date-fns";
import { AgGridReact } from "ag-grid-react";
import { DataContext } from "../../context/DataContext";
import CustomerActionModal from "./CustomerActionModal";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { Formik, Form, Field, FieldArray } from "formik";
import * as Yup from "yup";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

ModuleRegistry.registerModules([AllCommunityModule]);

// Optimized Debounce Hook
const useDebounce = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

// Custom cell renderers
// const CheckboxRenderer = (props) => {
//   return (
//     <Checkbox
//       checked={props.context?.selectedRows?.includes(props.data.id) || false}
//       onChange={(e) => {
//         props.context?.handleRowSelect(props.data.id, e.target.checked);
//       }}
//       sx={{
//         color: "#2563EB",
//         "&.Mui-checked": {
//           color: "#2563EB",
//         },
//       }}
//     />
//   );
// };

const ActionsRenderer = (props) => {
  const handleEdit = () => {
    props.context.handleEditCustomer(props.data);
  };

  const handleDelete = () => {
    props.context.setDeleteCustomerId(props.data.id);
    props.context.setDeleteConfirmOpen(true);
  };

  const handleView = () => {
    props.context.setSelectedCustomer(props.data);
    props.context.setModalOpen(true);
  };

  return (
    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
      <Tooltip title="View Details">
        <IconButton size="small" onClick={handleView}>
          <ViewIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Edit">
        <IconButton size="small" onClick={handleEdit}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton
          size="small"
          onClick={handleDelete}
          sx={{ color: "#EF4444" }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const FamilyMembersRenderer = (props) => {
  const [modalOpen, setModalOpen] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // SAFE: Ensure familyMembers is always an array
  const familyMembers = useMemo(() => {
    const data = props.data?.familyMembers;
    
    // If it's null, undefined, or not an array, return empty array
    if (!data) return [];
    if (!Array.isArray(data)) {
      // Try to parse if it's a string
      if (typeof data === 'string') {
        try {
          const parsed = JSON.parse(data);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
      return [];
    }
    return data;
  }, [props.data?.familyMembers]);

  const hasFamilyMembers = familyMembers.length > 0;

  // Helper to clean date string (remove apostrophe)
  const cleanDateString = (dateStr) => {
    if (!dateStr) return '';
    return dateStr.startsWith("'") ? dateStr.slice(1) : dateStr;
  };

  // Function to format date like "15 Oct" (similar to your other columns)
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    try {
      const cleanDateStr = cleanDateString(dateStr);
      const [day, month, year] = cleanDateStr.split('/');
      const date = new Date(year, month - 1, day);
      return format(date, "dd MMM yyyy");
    } catch {
      return cleanDateString(dateStr);
    }
  };

  if (!hasFamilyMembers) {
    return (
      <Chip
        label="No Family"
        size="small"
        sx={{
          backgroundColor: isDark
            ? alpha('#64748b', 0.2)
            : alpha('#64748b', 0.1),
          border: `1px solid ${alpha('#64748b', isDark ? 0.3 : 0.2)}`,
          color: isDark ? '#cbd5e1' : '#64748b',
          fontSize: '0.85rem',
          fontWeight: 500,
          cursor: 'default',
          borderRadius: '8px',
          '&:hover': {
            backgroundColor: isDark
              ? alpha('#64748b', 0.25)
              : alpha('#64748b', 0.15),
          }
        }}
      />
    );
  }

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={() => setModalOpen(true)}
        sx={{
          borderRadius: '10px',
          borderColor: isDark
            ? alpha('#8b5cf6', 0.4)
            : alpha('#8b5cf6', 0.3),
          backgroundColor: isDark
            ? alpha('#8b5cf6', 0.15)
            : alpha('#8b5cf6', 0.1),
          color: isDark ? '#c4b5fd' : '#8b5cf6',
          fontSize: '0.85rem',
          fontWeight: 700,
          textTransform: 'none',
          px: 1,
          py: 0.5,
          minWidth: 'auto',
          letterSpacing: '0.3px',
          transition: 'all 0.2s ease',
          boxShadow: isDark
            ? '0 2px 8px rgba(139, 92, 246, 0.1)'
            : '0 2px 8px rgba(139, 92, 246, 0.08)',
          '&:hover': {
            borderColor: isDark ? '#a78bfa' : '#8b5cf6',
            backgroundColor: isDark
              ? alpha('#8b5cf6', 0.25)
              : alpha('#8b5cf6', 0.15),
            transform: 'translateY(-1px)',
            boxShadow: isDark
              ? '0 4px 12px rgba(139, 92, 246, 0.2)'
              : '0 4px 12px rgba(139, 92, 246, 0.12)',
          },
          '&:active': {
            transform: 'translateY(0)',
          }
        }}
      >
        <Group sx={{
          fontSize: 16,
          mr: 1,
          color: isDark ? '#c4b5fd' : '#8b5cf6',
        }} />
        ({familyMembers.length})
      </Button>

      {/* Family Details Modal - Enhanced Styling */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: isDark
              ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(isDark ? '#334155' : '#e2e8f0', isDark ? 0.2 : 0.3)}`,
            boxShadow: isDark
              ? '0 20px 60px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05) inset'
              : '0 20px 60px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.8) inset',
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{
          p: 3,
          pb: 2,
          background: isDark
            ? 'linear-gradient(90deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.05) 100%)'
            : 'linear-gradient(90deg, rgba(139, 92, 246, 0.08) 0%, rgba(124, 58, 237, 0.04) 100%)',
          borderBottom: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.2 : 0.3)}`,
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <Box sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: isDark
                ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(124, 58, 237, 0.2) 100%)'
                : 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(124, 58, 237, 0.15) 100%)',
              border: `1.5px solid ${alpha(isDark ? '#8b5cf6' : '#8b5cf6', isDark ? 0.3 : 0.2)}`,
              boxShadow: `0 4px 12px ${alpha('#8b5cf6', isDark ? 0.15 : 0.1)}`,
            }}>
              <Group sx={{
                fontSize: 22,
                color: isDark ? '#c4b5fd' : '#8b5cf6',
              }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{
                fontWeight: 800,
                background: isDark
                  ? 'linear-gradient(135deg, #c4b5fd 0%, #a78bfa 100%)'
                  : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.02em',
                mb: 0.5,
              }}>
                Family Details
              </Typography>
              <Typography variant="body2" sx={{
                color: isDark ? '#94a3b8' : '#64748b',
                fontWeight: 500,
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}>
                <AccountCircle sx={{ fontSize: 16, opacity: 0.8 }} />
                {props.data?.name || 'Customer'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3, pt: 3 }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
            maxHeight: '60vh',
            overflowY: 'auto',
            pr: 1,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: isDark ? alpha('#1e293b', 0.5) : alpha('#f1f5f9', 0.5),
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: isDark ? '#475569' : '#cbd5e1',
              borderRadius: '10px',
              '&:hover': {
                background: isDark ? '#64748b' : '#94a3b8',
              }
            }
          }}>
            {familyMembers.map((member, index) => (
              <Card
                key={index}
                sx={{
                  mt: 2,
                  borderRadius: '16px',
                  background: isDark
                    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.7) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                  border: `1.5px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.3 : 0.4)}`,
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  boxShadow: isDark
                    ? '0 8px 32px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.05) inset'
                    : '0 8px 32px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(255, 255, 255, 0.8) inset',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    borderColor: alpha('#8b5cf6', isDark ? 0.4 : 0.3),
                    boxShadow: isDark
                      ? '0 12px 40px rgba(139, 92, 246, 0.15), 0 0 0 1px rgba(139, 92, 246, 0.1) inset'
                      : '0 12px 40px rgba(139, 92, 246, 0.1), 0 0 0 1px rgba(139, 92, 246, 0.05) inset',
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* Header with name and relationship */}
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 2,
                    pb: 2,
                    borderBottom: `1px dashed ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.3 : 0.4)}`,
                  }}>
                    <Box>
                      <Typography variant="h6" sx={{
                        fontWeight: 700,
                        color: isDark ? '#f1f5f9' : '#1e293b',
                        mb: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}>
                        {member.name || 'Unnamed Member'}
                        <Chip
                          label={`#${index + 1}`}
                          size="small"
                          sx={{
                            backgroundColor: isDark
                              ? alpha('#8b5cf6', 0.2)
                              : alpha('#8b5cf6', 0.1),
                            color: isDark ? '#c4b5fd' : '#8b5cf6',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 20,
                            px: 0.5,
                          }}
                        />
                      </Typography>
                      {member.relationship && (
                        <Chip
                          label={member.relationship}
                          size="small"
                          sx={{
                            backgroundColor: isDark
                              ? alpha('#10b981', 0.2)
                              : alpha('#10b981', 0.1),
                            color: isDark ? '#34d399' : '#10b981',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: 22,
                            border: `1px solid ${alpha(isDark ? '#10b981' : '#10b981', isDark ? 0.3 : 0.2)}`,
                          }}
                        />
                      )}
                    </Box>

                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: isDark
                        ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(124, 58, 237, 0.15) 100%)'
                        : 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
                      border: `1.5px solid ${alpha(isDark ? '#8b5cf6' : '#8b5cf6', isDark ? 0.2 : 0.15)}`,
                    }}>
                      <Typography sx={{
                        fontWeight: 700,
                        color: isDark ? '#c4b5fd' : '#8b5cf6',
                        fontSize: '1.1rem',
                      }}>
                        {member.name?.charAt(0)?.toUpperCase() || 'F'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Details Grid */}
                  <Grid container spacing={2.5}>
                    {/* Contact */}
                    {member.contact && (
                      <Grid item xs={12}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 2,
                          borderRadius: '12px',
                          background: isDark
                            ? alpha('#1e293b', 0.5)
                            : alpha('#f8fafc', 0.8),
                          border: `1px solid ${alpha(isDark ? '#334155' : '#e2e8f0', isDark ? 0.2 : 0.3)}`,
                        }}>
                          <Box sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '9px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: isDark
                              ? alpha('#3b82f6', 0.2)
                              : alpha('#3b82f6', 0.1),
                            border: `1px solid ${alpha(isDark ? '#3b82f6' : '#3b82f6', isDark ? 0.2 : 0.15)}`,
                          }}>
                            <Typography sx={{
                              fontSize: 18,
                              color: isDark ? '#60a5fa' : '#3b82f6',
                              fontWeight: 600,
                            }}>
                              📞
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{
                              fontWeight: 600,
                              color: isDark ? '#94a3b8' : '#64748b',
                              fontSize: '0.75rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              mb: 0.5,
                              display: 'block'
                            }}>
                              Contact Number
                            </Typography>
                            <Typography variant="body2" sx={{
                              color: isDark ? '#e2e8f0' : '#1e293b',
                              fontWeight: 600,
                              fontSize: '1rem',
                              fontFamily: 'monospace',
                            }}>
                              {member.contact}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )}

                    {/* DOB and Anniversary in a row */}
                    <Grid item xs={6}>
                      {member.dob && (
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 2,
                          borderRadius: '12px',
                          background: isDark
                            ? alpha('#1e293b', 0.5)
                            : alpha('#f8fafc', 0.8),
                          border: `1px solid ${alpha(isDark ? '#334155' : '#e2e8f0', isDark ? 0.2 : 0.3)}`,
                          height: '100%',
                        }}>
                          <Box sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: isDark
                              ? alpha('#f59e0b', 0.2)
                              : alpha('#f59e0b', 0.1),
                            border: `1px solid ${alpha(isDark ? '#f59e0b' : '#f59e0b', isDark ? 0.2 : 0.15)}`,
                          }}>
                            <Event sx={{
                              fontSize: 16,
                              color: isDark ? '#fbbf24' : '#f59e0b',
                            }} />
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{
                              fontWeight: 600,
                              color: isDark ? '#94a3b8' : '#64748b',
                              fontSize: '0.75rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              mb: 0.5,
                              display: 'block'
                            }}>
                              Date of Birth
                            </Typography>
                            <Typography variant="body2" sx={{
                              color: isDark ? '#e2e8f0' : '#1e293b',
                              fontWeight: 600,
                              fontSize: '0.9rem',
                            }}>
                              {formatDateForDisplay(member.dob)}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Grid>

                    <Grid item xs={6}>
                      {member.anniversary && (
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 2,
                          borderRadius: '12px',
                          background: isDark
                            ? alpha('#1e293b', 0.5)
                            : alpha('#f8fafc', 0.8),
                          border: `1px solid ${alpha(isDark ? '#334155' : '#e2e8f0', isDark ? 0.2 : 0.3)}`,
                          height: '100%',
                        }}>
                          <Box sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: isDark
                              ? alpha('#ec4899', 0.2)
                              : alpha('#ec4899', 0.1),
                            border: `1px solid ${alpha(isDark ? '#ec4899' : '#ec4899', isDark ? 0.2 : 0.15)}`,
                          }}>
                            <Typography sx={{
                              fontSize: 16,
                              color: isDark ? '#f472b6' : '#ec4899',
                              fontWeight: 600,
                            }}>
                              ❤️
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{
                              fontWeight: 600,
                              color: isDark ? '#94a3b8' : '#64748b',
                              fontSize: '0.75rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              mb: 0.5,
                              display: 'block'
                            }}>
                              Anniversary
                            </Typography>
                            <Typography variant="body2" sx={{
                              color: isDark ? '#e2e8f0' : '#1e293b',
                              fontWeight: 600,
                              fontSize: '0.9rem',
                            }}>
                              {formatDateForDisplay(member.anniversary)}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Box>
        </DialogContent>

        <DialogActions sx={{
          p: 3,
          pt: 2,
          borderTop: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', isDark ? 0.2 : 0.3)}`,
          background: isDark
            ? alpha('#0f172a', 0.5)
            : alpha('#f8fafc', 0.8),
        }}>
          <Button
            onClick={() => setModalOpen(false)}
            variant="outlined"
            size="large"
            sx={{
              borderRadius: '12px',
              borderColor: isDark ? '#475569' : '#cbd5e1',
              color: isDark ? '#cbd5e1' : '#475569',
              fontWeight: 600,
              textTransform: 'none',
              px: 4,
              py: 1,
              fontSize: '0.9rem',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: isDark ? '#64748b' : '#94a3b8',
                backgroundColor: isDark
                  ? alpha('#475569', 0.2)
                  : alpha('#cbd5e1', 0.1),
                transform: 'translateY(-1px)',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const NameRenderer = (props) => {
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {props.value}
      </Typography>
      {props.data.emailId && (
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block", mt: 0.5 }}
        >
          {props.data.emailId}
        </Typography>
      )}
    </Box>
  );
};

// Validation Schema - FIXED
const CustomerSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name is too long")
    .required("Name is required"),
  emailId: Yup.string()
    .email("Invalid email address")
    .nullable()
    .transform((value) => (value === "" ? null : value)),
  contact: Yup.string()
    .required("Contact number is required")
    .matches(/^[0-9+\-\s()]+$/, "Invalid contact number"),
  address: Yup.string()
    .max(500, "Address is too long")
    .nullable()
    .transform((value) => (value === "" ? null : value)),
  dob: Yup.mixed() // Changed from Yup.date() to Yup.mixed()
    .nullable()
    .transform((value) => {
      // Handle Invalid Date objects
      if (value instanceof Date && isNaN(value.getTime())) return null;
      if (value === "" || value === undefined) return null;
      return value;
    })
    .test("is-valid-date", "Invalid date", (value) => {
      if (!value) return true;
      const date = new Date(value);
      return !isNaN(date.getTime());
    }),
  anniversary: Yup.mixed() // Changed from Yup.date() to Yup.mixed()
    .nullable()
    .transform((value) => {
      // Handle Invalid Date objects
      if (value instanceof Date && isNaN(value.getTime())) return null;
      if (value === "" || value === undefined) return null;
      return value;
    })
    .test("is-valid-date", "Invalid date", (value) => {
      if (!value) return true;
      const date = new Date(value);
      return !isNaN(date.getTime());
    }),
  openingBalance: Yup.number()
    .typeError("Opening balance must be a number")
    .min(0, "Balance cannot be negative")
    .nullable()
    .transform((value) => (value === "" ? null : value)),
  familyMembers: Yup.array().of(
    Yup.object().shape({
      name: Yup.string()
        .max(100, "Name is too long")
        .nullable()
        .transform((value) => (value === "" ? null : value)),
      contact: Yup.string()
        .nullable()
        .transform((value) => (value === "" ? null : value))
        .matches(/^[0-9+\-\s()]*$/, "Invalid contact number"),
      relationship: Yup.string()
        .nullable()
        .transform((value) => (value === "" ? null : value)),
      dob: Yup.mixed() // Changed from Yup.date() to Yup.mixed()
        .nullable()
        .transform((value) => {
          if (value instanceof Date && isNaN(value.getTime())) return null;
          if (value === "" || value === undefined) return null;
          return value;
        })
        .test("is-valid-date", "Invalid date", (value) => {
          if (!value) return true;
          const date = new Date(value);
          return !isNaN(date.getTime());
        }),
      anniversary: Yup.mixed() // Changed from Yup.date() to Yup.mixed()
        .nullable()
        .transform((value) => {
          if (value instanceof Date && isNaN(value.getTime())) return null;
          if (value === "" || value === undefined) return null;
          return value;
        })
        .test("is-valid-date", "Invalid date", (value) => {
          if (!value) return true;
          const date = new Date(value);
          return !isNaN(date.getTime());
        }),
    })
  ),
});

// Initial values for Formik
const initialValues = {
  name: "",
  emailId: "",
  contact: "",
  address: "",
  dob: null,
  anniversary: null,
  openingBalance: "",
  familyMembers: [],
};

// Customer Form Component
const CustomerForm = ({
  formik,
  isDark,
  isSaving,
  editingCustomer,
  handleSaveCustomer,
  handleInput, // Added handleInput prop
  customers
}) => {
  const [contactError, setContactError] = useState('');
  const isCheckingContact = false;

  useEffect(() => {
    const checkContact = () => {
      const contact = formik.values.contact;
      const excludeId = editingCustomer?.id;

      // Clear error if contact is empty
      if (!contact) {
        setContactError('');
        // Clear Formik error too
        formik.setFieldError('contact', '');
        return;
      }

      // Check for minimum length
      if (contact.length < 10) {
        setContactError('');
        formik.setFieldError('contact', 'Contact number must be at least 10 digits');
        return;
      }

      // Check for duplicates in local customers data
      const duplicate = customers?.find(
        c => c.contact?.toString() === contact?.toString() &&
          c.id !== excludeId
      );

      if (duplicate) {
        // Set the custom error
        setContactError('This phone number is already registered.');
        // IMPORTANT: Clear Formik error to avoid duplication
        formik.setFieldError('contact', '');
      } else {
        // Clear both errors
        setContactError('');
        formik.setFieldError('contact', '');
      }
    };

    const timer = setTimeout(checkContact, 500);
    return () => clearTimeout(timer);
  }, [formik.values.contact, editingCustomer?.id, customers, formik]);
  return (
    <Form>
      <Box
        sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}
      >
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                background: isDark
                  ? "linear-gradient(135deg, #93C5FD 0%, #60A5FA 100%)"
                  : "linear-gradient(135deg, #1E293B 0%, #334155 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
              }}
            >
              {editingCustomer ? "✏️ Edit Customer" : "👤 Add New Customer"}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: isDark ? "#94a3b8" : "#64748b",
              fontWeight: 400,
              fontSize: "0.875rem",
            }}
          >
            {editingCustomer
              ? "Update customer information"
              : "Enter customer details"}
          </Typography>
        </Box>

        {/* Form Content - Scrollable Area */}
        <Box sx={{ flex: 1, overflowY: "auto", pr: 1, mb: 3, pt: 2 }}>
          {/* Personal Details Card */}
          <Card
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 2,
              backdropFilter: "blur(10px)",
              backgroundColor: alpha(
                isDark ? "#1e293b" : "#ffffff",
                isDark ? 0.4 : 0.6
              ),
              border: `1px solid ${alpha(
                isDark ? "#334155" : "#e2e8f0",
                isDark ? 0.2 : 0.3
              )}`,
              boxShadow: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: alpha(
                  isDark ? "#475569" : "#cbd5e1",
                  isDark ? 0.3 : 0.4
                ),
              },
            }}
          >
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: isDark
                      ? "linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.2) 100%)"
                      : "linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)",
                    border: `1px solid ${alpha(
                      isDark ? "#3b82f6" : "#10b981",
                      isDark ? 0.2 : 0.15
                    )}`,
                  }}
                >
                  <AccountCircle
                    sx={{
                      color: isDark ? "#60a5fa" : "#10b981",
                      fontSize: 18,
                    }}
                  />
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: isDark ? "#e2e8f0" : "#1e293b",
                    fontSize: "0.95rem",
                  }}
                >
                  Personal Details
                </Typography>
              </Box>

              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mb: 0.75,
                      fontWeight: 500,
                      color: isDark ? "#cbd5e1" : "#475569",
                      fontSize: "0.8rem",
                    }}
                  >
                    Name <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <Field name="name">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Enter full name"
                        disabled={isSaving}
                        size="small"
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? "#0f172a" : "#ffffff",
                              isDark ? 0.6 : 0.8
                            ),
                            border: `1px solid ${alpha(
                              isDark ? "#475569" : "#e2e8f0",
                              isDark ? 0.3 : 0.4
                            )}`,
                            "&:hover": {
                              borderColor: alpha(
                                isDark ? "#60a5fa" : "#3b82f6",
                                isDark ? 0.5 : 0.6
                              ),
                              backgroundColor: alpha(
                                isDark ? "#0f172a" : "#ffffff",
                                isDark ? 0.7 : 0.9
                              ),
                            },
                            "&.Mui-focused": {
                              borderColor: isDark ? "#60a5fa" : "#3b82f6",
                              borderWidth: "1.5px",
                              backgroundColor: alpha(
                                isDark ? "#0f172a" : "#ffffff",
                                isDark ? 0.7 : 0.9
                              ),
                            },
                          },
                          "& .MuiOutlinedInput-input": {
                            color: isDark ? "#e2e8f0" : "#1e293b",
                            fontSize: "0.875rem",
                            padding: "10px 12px",
                          },
                          "& .MuiFormHelperText-root": {
                            marginLeft: 0,
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    )}
                  </Field>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mb: 0.75,
                      fontWeight: 500,
                      color: isDark ? "#cbd5e1" : "#475569",
                      fontSize: "0.8rem",
                    }}
                  >
                    Email Address
                  </Typography>
                  <Field name="emailId">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        type="email"
                        placeholder="customer@example.com"
                        disabled={isSaving}
                        size="small"
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? "#0f172a" : "#ffffff",
                              isDark ? 0.6 : 0.8
                            ),
                            border: `1px solid ${alpha(
                              isDark ? "#475569" : "#e2e8f0",
                              isDark ? 0.3 : 0.4
                            )}`,
                            "&:hover": {
                              borderColor: alpha(
                                isDark ? "#60a5fa" : "#3b82f6",
                                isDark ? 0.5 : 0.6
                              ),
                            },
                          },
                          "& .MuiOutlinedInput-input": {
                            color: isDark ? "#e2e8f0" : "#1e293b",
                            fontSize: "0.875rem",
                            padding: "10px 12px",
                          },
                          "& .MuiFormHelperText-root": {
                            marginLeft: 0,
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    )}
                  </Field>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mb: 0.75,
                      fontWeight: 500,
                      color: isDark ? "#cbd5e1" : "#475569",
                      fontSize: "0.8rem",
                    }}
                  >
                    Contact Number <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <Field name="contact">
                    {({ field, form, meta }) => {
                      // Determine which error to show
                      const showError = (meta.touched || formik.submitCount > 0);

                      // Only use contactError if meta.error is empty
                      let errorMessage = '';
                      if (contactError && !meta.error) {
                        errorMessage = contactError;
                      } else if (meta.error) {
                        errorMessage = meta.error;
                      }

                      const shouldShowError = showError && errorMessage;

                      return (
                        <Box>
                          <TextField
                            {...field}
                            fullWidth
                            type="text"
                            placeholder="+91 98765 43210"
                            disabled={isSaving}
                            size="small"
                            error={!!shouldShowError}
                            helperText={shouldShowError || ''}
                            onChange={(e) => handleInput(e, "contact", form)}
                            onKeyDown={(e) => {
                              if (/[a-zA-Z]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            inputMode="tel"
                            InputProps={{
                              endAdornment: isCheckingContact && (
                                <InputAdornment position="end">
                                  <CircularProgress size={20} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 1.5,
                                backgroundColor: alpha(
                                  isDark ? "#0f172a" : "#ffffff",
                                  isDark ? 0.6 : 0.8
                                ),
                                border: `1px solid ${alpha(
                                  isDark ? "#475569" : "#e2e8f0",
                                  isDark ? 0.3 : 0.4
                                )}`,
                                "&:hover": {
                                  borderColor: alpha(
                                    isDark ? "#60a5fa" : "#3b82f6",
                                    isDark ? 0.5 : 0.6
                                  ),
                                },
                                // Dynamic border color based on validation
                                ...(shouldShowError && {
                                  borderColor: '#ef4444',
                                }),
                                ...(!shouldShowError && formik.values.contact && formik.values.contact.length >= 10 && {
                                  borderColor: '#22c55e',
                                }),
                              },
                              "& .MuiOutlinedInput-input": {
                                color: isDark ? "#e2e8f0" : "#1e293b",
                                fontSize: "0.875rem",
                                padding: "10px 12px",
                              },
                              "& .MuiFormHelperText-root": {
                                marginLeft: 0,
                                fontSize: "0.75rem",
                              },
                            }}
                          />
                        </Box>
                      );
                    }}
                  </Field>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mb: 0.75,
                      fontWeight: 500,
                      color: isDark ? "#cbd5e1" : "#475569",
                      fontSize: "0.8rem",
                    }}
                  >
                    Address
                  </Typography>
                  <Field name="address">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Street, City, State, Pincode"
                        multiline
                        rows={3}
                        disabled={isSaving}
                        size="small"
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? "#0f172a" : "#ffffff",
                              isDark ? 0.6 : 0.8
                            ),
                            border: `1px solid ${alpha(
                              isDark ? "#475569" : "#e2e8f0",
                              isDark ? 0.3 : 0.4
                            )}`,
                            "&:hover": {
                              borderColor: alpha(
                                isDark ? "#60a5fa" : "#3b82f6",
                                isDark ? 0.5 : 0.6
                              ),
                            },
                          },
                          "& .MuiOutlinedInput-input": {
                            color: isDark ? "#e2e8f0" : "#1e293b",
                            fontSize: "0.875rem",
                            padding: "10px 12px",
                          },
                          "& .MuiFormHelperText-root": {
                            marginLeft: 0,
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    )}
                  </Field>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Opening Balances Card */}
          <Card
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 2,
              backdropFilter: "blur(10px)",
              backgroundColor: alpha(
                isDark ? "#1e293b" : "#ffffff",
                isDark ? 0.4 : 0.6
              ),
              border: `1px solid ${alpha(
                isDark ? "#334155" : "#e2e8f0",
                isDark ? 0.2 : 0.3
              )}`,
              boxShadow: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: alpha(
                  isDark ? "#475569" : "#cbd5e1",
                  isDark ? 0.3 : 0.4
                ),
              },
            }}
          >
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: isDark
                      ? "linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.2) 100%)"
                      : "linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.15) 100%)",
                    border: `1px solid ${alpha(
                      isDark ? "#f59e0b" : "#f59e0b",
                      isDark ? 0.2 : 0.15
                    )}`,
                  }}
                >
                  <AccountBalanceWallet
                    sx={{
                      color: isDark ? "#fbbf24" : "#f59e0b",
                      fontSize: 18,
                    }}
                  />
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: isDark ? "#e2e8f0" : "#1e293b",
                    fontSize: "0.95rem",
                  }}
                >
                  Opening Balances
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mb: 0.75,
                        fontWeight: 500,
                        color: isDark ? "#cbd5e1" : "#475569",
                        fontSize: "0.8rem",
                      }}
                    >
                      Opening Balance
                    </Typography>
                    <Field name="openingBalance">
                      {({ field, form, meta }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="text"
                          placeholder="0.00"
                          disabled={isSaving || !!editingCustomer}
                          size="small"
                          error={meta.touched && !!meta.error}
                          helperText={meta.touched && meta.error}
                          onChange={(e) => handleInput(e, "openingBalance", form)}
                          onKeyDown={(e) => {
                            // Allow: numbers, decimal point, backspace, delete, arrow keys, tab
                            const allowedKeys = [
                              '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
                              '.', 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight',
                              'ArrowUp', 'ArrowDown', 'Tab'
                            ];

                            if (!allowedKeys.includes(e.key)) {
                              e.preventDefault();
                            }

                            // Prevent multiple decimal points
                            if (e.key === '.' && field.value.includes('.')) {
                              e.preventDefault();
                            }
                          }}
                          inputMode="decimal"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AttachMoney
                                  sx={{
                                    fontSize: 16,
                                    color: isDark ? "#fbbf24" : "#f59e0b",
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1.5,
                              backgroundColor: alpha(
                                isDark ? "#0f172a" : "#ffffff",
                                isDark ? 0.6 : 0.8
                              ),
                              border: `1px solid ${alpha(
                                isDark ? "#475569" : "#e2e8f0",
                                isDark ? 0.3 : 0.4
                              )}`,
                              "&:hover": {
                                borderColor: alpha(
                                  isDark ? "#fbbf24" : "#f59e0b",
                                  isDark ? 0.5 : 0.6
                                ),
                              },
                            },
                            "& .MuiOutlinedInput-input": {
                              color: isDark ? "#e2e8f0" : "#1e293b",
                              fontSize: "0.875rem",
                              padding: "10px 12px",
                            },
                            "& .MuiFormHelperText-root": {
                              marginLeft: 0,
                              fontSize: "0.75rem",
                            },
                          }}
                        />
                      )}
                    </Field>
                  </Box>
                </Grid>
              </Grid>

              <Typography
                variant="caption"
                sx={{
                  mt: 1.5,
                  display: "block",
                  color: isDark ? "#94A3B8" : "#64748B",
                  fontSize: "0.75rem",
                  fontStyle: "italic",
                }}
              >
                Optional: Initial balances for customer account. Leave as 0 if
                none.
              </Typography>
            </CardContent>
          </Card>

          {/* Family Members Card */}
          <Card
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 2,
              backdropFilter: "blur(10px)",
              backgroundColor: alpha(
                isDark ? "#1e293b" : "#ffffff",
                isDark ? 0.4 : 0.6
              ),
              border: `1px solid ${alpha(
                isDark ? "#334155" : "#e2e8f0",
                isDark ? 0.2 : 0.3
              )}`,
              boxShadow: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: alpha(
                  isDark ? "#475569" : "#cbd5e1",
                  isDark ? 0.3 : 0.4
                ),
              },
            }}
          >
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: isDark
                        ? "linear-gradient(135deg, rgba(168, 85, 247, 0.2) 0%, rgba(147, 51, 234, 0.2) 100%)"
                        : "linear-gradient(135deg, rgba(168, 85, 247, 0.15) 0%, rgba(147, 51, 234, 0.15) 100%)",
                      border: `1px solid ${alpha(
                        isDark ? "#a855f7" : "#a855f7",
                        isDark ? 0.2 : 0.15
                      )}`,
                    }}
                  >
                    <Group
                      sx={{
                        color: isDark ? "#c084fc" : "#a855f7",
                        fontSize: 18,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: isDark ? "#e2e8f0" : "#1e293b",
                      fontSize: "0.95rem",
                    }}
                  >
                    Family Members
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    formik.setFieldValue("familyMembers", [
                      ...formik.values.familyMembers,
                      {
                        name: "",
                        contact: "",
                        relationship: "",
                        dob: "",
                        anniversary: "",
                      },
                    ])
                  }
                  disabled={isSaving}
                  startIcon={<AddIcon />}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: "none",
                    backgroundColor: alpha(
                      isDark ? "#1e293b" : "#ffffff",
                      isDark ? 0.4 : 0.8
                    ),
                    border: `1.5px solid ${alpha(
                      isDark ? "#475569" : "#cbd5e1",
                      isDark ? 0.3 : 0.4
                    )}`,
                    color: isDark ? "#e2e8f0" : "#475569",
                    fontSize: "0.75rem",
                    px: 2,
                    py: 0.75,
                    "&:hover": {
                      backgroundColor: alpha(
                        isDark ? "#1e293b" : "#ffffff",
                        isDark ? 0.6 : 0.95
                      ),
                      borderColor: alpha(
                        isDark ? "#60a5fa" : "#3b82f6",
                        isDark ? 0.5 : 0.6
                      ),
                      transform: "translateY(-1px)",
                    },
                    "&.Mui-disabled": {
                      opacity: 0.5,
                    },
                  }}
                >
                  Add Member
                </Button>
              </Box>

              {/* Family Members List */}
              <Stack spacing={2}>
                <FieldArray name="familyMembers">
                  {({ remove, push }) => (
                    <>
                      {formik.values.familyMembers &&
                        formik.values.familyMembers.length > 0 ? (
                        formik.values.familyMembers.map((member, index) => (
                          <Box
                            key={index}
                            sx={{
                              p: 2,
                              borderRadius: 1.5,
                              backgroundColor: alpha(
                                isDark ? "#0f172a" : "#f8fafc",
                                isDark ? 0.5 : 0.5
                              ),
                              border: `1px solid ${alpha(
                                isDark ? "#334155" : "#e2e8f0",
                                isDark ? 0.2 : 0.3
                              )}`,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                mb: 1.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 600,
                                  color: isDark ? "#cbd5e1" : "#475569",
                                  fontSize: "0.8rem",
                                }}
                              >
                                Family Member #{index + 1}
                              </Typography>
                              {index > 0 && (
                                <IconButton
                                  size="small"
                                  onClick={() => remove(index)}
                                  disabled={isSaving}
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    color: "#ef4444",
                                    "&:hover": {
                                      backgroundColor: alpha("#ef4444", 0.1),
                                    },
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>

                            <Grid container spacing={1.5}>
                              <Grid item xs={12}>
                                <Box>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: "block",
                                      mb: 0.5,
                                      fontWeight: 500,
                                      color: isDark ? "#94a3b8" : "#64748b",
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    Name
                                  </Typography>
                                  <Field name={`familyMembers.${index}.name`}>
                                    {({ field, meta }) => (
                                      <TextField
                                        {...field}
                                        fullWidth
                                        placeholder="Enter family member name"
                                        disabled={isSaving}
                                        size="small"
                                        error={meta.touched && !!meta.error}
                                        helperText={meta.touched && meta.error}
                                        sx={{
                                          "& .MuiOutlinedInput-root": {
                                            borderRadius: 1,
                                            backgroundColor: alpha(
                                              isDark ? "#0f172a" : "#ffffff",
                                              isDark ? 0.6 : 0.9
                                            ),
                                            border: `1px solid ${alpha(
                                              isDark ? "#475569" : "#e2e8f0",
                                              isDark ? 0.3 : 0.4
                                            )}`,
                                            "&:hover": {
                                              borderColor: alpha(
                                                isDark ? "#60a5fa" : "#3b82f6",
                                                isDark ? 0.5 : 0.6
                                              ),
                                            },
                                          },
                                          "& .MuiOutlinedInput-input": {
                                            color: isDark ? "#e2e8f0" : "#1e293b",
                                            fontSize: "0.813rem",
                                            padding: "8px 10px",
                                          },
                                          "& .MuiFormHelperText-root": {
                                            marginLeft: 0,
                                            fontSize: "0.75rem",
                                          },
                                        }}
                                      />
                                    )}
                                  </Field>
                                </Box>
                              </Grid>

                              <Grid item xs={12}>
                                <Box>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: "block",
                                      mb: 0.5,
                                      fontWeight: 500,
                                      color: isDark ? "#94a3b8" : "#64748b",
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    Contact Number
                                  </Typography>
                                  <Field name={`familyMembers.${index}.contact`}>
                                    {({ field, form, meta }) => (
                                      <TextField
                                        {...field}
                                        fullWidth
                                        type="text"
                                        placeholder="Enter contact number"
                                        disabled={isSaving}
                                        size="small"
                                        error={meta.touched && !!meta.error}
                                        helperText={meta.touched && meta.error}
                                        onChange={(e) => handleInput(e, `familyMembers.${index}.contact`, form)}
                                        onKeyDown={(e) => {
                                          // Prevent alphabets from being typed
                                          if (/[a-zA-Z]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                                            e.preventDefault();
                                          }
                                        }}
                                        inputMode="tel"
                                        sx={{
                                          "& .MuiOutlinedInput-root": {
                                            borderRadius: 1,
                                            backgroundColor: alpha(
                                              isDark ? "#0f172a" : "#ffffff",
                                              isDark ? 0.6 : 0.9
                                            ),
                                            border: `1px solid ${alpha(
                                              isDark ? "#475569" : "#e2e8f0",
                                              isDark ? 0.3 : 0.4
                                            )}`,
                                            "&:hover": {
                                              borderColor: alpha(
                                                isDark ? "#60a5fa" : "#3b82f6",
                                                isDark ? 0.5 : 0.6
                                              ),
                                            },
                                          },
                                          "& .MuiOutlinedInput-input": {
                                            color: isDark ? "#e2e8f0" : "#1e293b",
                                            fontSize: "0.813rem",
                                            padding: "8px 10px",
                                          },
                                          "& .MuiFormHelperText-root": {
                                            marginLeft: 0,
                                            fontSize: "0.75rem",
                                          },
                                        }}
                                      />
                                    )}
                                  </Field>
                                </Box>
                              </Grid>

                              <Grid item xs={12}>
                                <Box>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: "block",
                                      mb: 0.5,
                                      fontWeight: 500,
                                      color: isDark ? "#94a3b8" : "#64748b",
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    Relationship
                                  </Typography>
                                  <Field
                                    name={`familyMembers.${index}.relationship`}
                                  >
                                    {({ field, meta }) => (
                                      <Select
                                        {...field}
                                        fullWidth
                                        disabled={isSaving}
                                        size="small"
                                        displayEmpty
                                        error={meta.touched && !!meta.error}
                                        sx={{
                                          borderRadius: 1,
                                          backgroundColor: alpha(
                                            isDark ? "#0f172a" : "#ffffff",
                                            isDark ? 0.6 : 0.9
                                          ),
                                          border: `1px solid ${alpha(
                                            isDark ? "#475569" : "#e2e8f0",
                                            isDark ? 0.3 : 0.4
                                          )}`,
                                          "&:hover": {
                                            borderColor: alpha(
                                              isDark ? "#60a5fa" : "#3b82f6",
                                              isDark ? 0.5 : 0.6
                                            ),
                                          },
                                          "& .MuiSelect-select": {
                                            color: isDark ? "#e2e8f0" : "#1e293b",
                                            fontSize: "0.813rem",
                                            padding: "8px 10px",
                                          },
                                        }}
                                      >
                                        <MenuItem value="">
                                          Select Relationship
                                        </MenuItem>
                                        <MenuItem value="Spouse">Spouse</MenuItem>
                                        <MenuItem value="Son">Son</MenuItem>
                                        <MenuItem value="Daughter">
                                          Daughter
                                        </MenuItem>
                                        <MenuItem value="Father">Father</MenuItem>
                                        <MenuItem value="Mother">Mother</MenuItem>
                                        <MenuItem value="Other">Other</MenuItem>
                                      </Select>
                                    )}
                                  </Field>
                                </Box>
                              </Grid>

                              <Grid item xs={6}>
                                <Box>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: "block",
                                      mb: 0.5,
                                      fontWeight: 500,
                                      color: isDark ? "#94a3b8" : "#64748b",
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    Date of Birth
                                  </Typography>
                                  {/* DOB Field - DatePicker for family members */}
                                  <Field name={`familyMembers.${index}.dob`}>
                                    {({ field, form, meta }) => (
                                      <DatePicker
                                        value={(field.value)?.toString().startsWith("'") ? field.value.slice(1) : field.value}
                                        onChange={(date) =>
                                          form.setFieldValue(field.name, date)
                                        }
                                        onBlur={field.onBlur}
                                        disabled={isSaving}
                                        enableAccessibleFieldDOMStructure={false}
                                        slotProps={{
                                          textField: {
                                            fullWidth: true,
                                            size: "small",
                                            error: meta.touched && !!meta.error,
                                            helperText:
                                              meta.touched && meta.error,
                                            placeholder: "DD/MM/YYYY",
                                            variant: "outlined",
                                            sx: {
                                              "& .MuiOutlinedInput-root": {
                                                borderRadius: 1,
                                                backgroundColor: alpha(
                                                  isDark ? "#0f172a" : "#ffffff",
                                                  isDark ? 0.6 : 0.9
                                                ),
                                                border: `1px solid ${alpha(
                                                  isDark ? "#475569" : "#e2e8f0",
                                                  isDark ? 0.3 : 0.4
                                                )}`,
                                                "&:hover": {
                                                  borderColor: alpha(
                                                    isDark
                                                      ? "#60a5fa"
                                                      : "#3b82f6",
                                                    isDark ? 0.5 : 0.6
                                                  ),
                                                  backgroundColor: alpha(
                                                    isDark
                                                      ? "#0f172a"
                                                      : "#ffffff",
                                                    isDark ? 0.7 : 0.95
                                                  ),
                                                },
                                                "&.Mui-focused": {
                                                  borderColor: isDark
                                                    ? "#60a5fa"
                                                    : "#3b82f6",
                                                  borderWidth: "1.5px",
                                                  backgroundColor: alpha(
                                                    isDark
                                                      ? "#0f172a"
                                                      : "#ffffff",
                                                    isDark ? 0.7 : 0.95
                                                  ),
                                                },
                                              },
                                              "& .MuiOutlinedInput-input": {
                                                color: isDark
                                                  ? "#e2e8f0"
                                                  : "#1e293b",
                                                fontSize: "0.813rem",
                                                padding: "8px 10px",
                                              },
                                              "& .MuiFormHelperText-root": {
                                                marginLeft: 0,
                                                fontSize: "0.75rem",
                                              },
                                            },
                                          },
                                          field: {
                                            clearable: true,
                                            format: "dd/MM/yyyy",
                                          },
                                        }}
                                        sx={{
                                          width: "100%",
                                          "& .MuiIconButton-root": {
                                            color: isDark ? "#60a5fa" : "#3b82f6",
                                          },
                                        }}
                                      />
                                    )}
                                  </Field>
                                </Box>
                              </Grid>

                              <Grid item xs={6}>
                                <Box>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: "block",
                                      mb: 0.5,
                                      fontWeight: 500,
                                      color: isDark ? "#94a3b8" : "#64748b",
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    Anniversary Date
                                  </Typography>
                                  {/* Anniversary Field - DatePicker for family members */}
                                  <Field
                                    name={`familyMembers.${index}.anniversary`}
                                  >
                                    {({ field, form, meta }) => (
                                      <DatePicker
                                        value={field.value}
                                        onChange={(date) =>
                                          form.setFieldValue(field.name, date)
                                        }
                                        onBlur={field.onBlur}
                                        disabled={isSaving}
                                        enableAccessibleFieldDOMStructure={false}
                                        slotProps={{
                                          textField: {
                                            fullWidth: true,
                                            size: "small",
                                            error: meta.touched && !!meta.error,
                                            helperText:
                                              meta.touched && meta.error,
                                            placeholder: "DD/MM/YYYY",
                                            variant: "outlined",
                                            sx: {
                                              "& .MuiOutlinedInput-root": {
                                                borderRadius: 1,
                                                backgroundColor: alpha(
                                                  isDark ? "#0f172a" : "#ffffff",
                                                  isDark ? 0.6 : 0.9
                                                ),
                                                border: `1px solid ${alpha(
                                                  isDark ? "#475569" : "#e2e8f0",
                                                  isDark ? 0.3 : 0.4
                                                )}`,
                                                "&:hover": {
                                                  borderColor: alpha(
                                                    isDark
                                                      ? "#60a5fa"
                                                      : "#3b82f6",
                                                    isDark ? 0.5 : 0.6
                                                  ),
                                                  backgroundColor: alpha(
                                                    isDark
                                                      ? "#0f172a"
                                                      : "#ffffff",
                                                    isDark ? 0.7 : 0.95
                                                  ),
                                                },
                                                "&.Mui-focused": {
                                                  borderColor: isDark
                                                    ? "#60a5fa"
                                                    : "#3b82f6",
                                                  borderWidth: "1.5px",
                                                  backgroundColor: alpha(
                                                    isDark
                                                      ? "#0f172a"
                                                      : "#ffffff",
                                                    isDark ? 0.7 : 0.95
                                                  ),
                                                },
                                              },
                                              "& .MuiOutlinedInput-input": {
                                                color: isDark
                                                  ? "#e2e8f0"
                                                  : "#1e293b",
                                                fontSize: "0.813rem",
                                                padding: "8px 10px",
                                              },
                                              "& .MuiFormHelperText-root": {
                                                marginLeft: 0,
                                                fontSize: "0.75rem",
                                              },
                                            },
                                          },
                                          field: {
                                            clearable: true,
                                            format: "dd/MM/yyyy",
                                          },
                                        }}
                                        sx={{
                                          width: "100%",
                                          "& .MuiIconButton-root": {
                                            color: isDark ? "#60a5fa" : "#3b82f6",
                                          },
                                        }}
                                      />
                                    )}
                                  </Field>
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        ))
                      ) : (
                        <Box
                          sx={{
                            p: 3,
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? "#0f172a" : "#f8fafc",
                              isDark ? 0.5 : 0.5
                            ),
                            border: `1px dashed ${alpha(
                              isDark ? "#475569" : "#cbd5e1",
                              isDark ? 0.3 : 0.4
                            )}`,
                            textAlign: "center",
                          }}
                        >
                          <Group
                            sx={{
                              fontSize: 32,
                              color: isDark ? "#475569" : "#94a3b8",
                              mb: 1,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: isDark ? "#94a3b8" : "#64748b",
                              mb: 0.5,
                            }}
                          >
                            No family members added yet
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark ? "#64748b" : "#94a3b8",
                            }}
                          >
                            Click "Add Member" to add family details
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                </FieldArray>
              </Stack>
            </CardContent>
          </Card>

          {/* Additional Details Card */}
          <Card
            elevation={0}
            sx={{
              borderRadius: 2,
              backdropFilter: "blur(10px)",
              backgroundColor: alpha(
                isDark ? "#1e293b" : "#ffffff",
                isDark ? 0.4 : 0.6
              ),
              border: `1px solid ${alpha(
                isDark ? "#334155" : "#e2e8f0",
                isDark ? 0.2 : 0.3
              )}`,
              boxShadow: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: alpha(
                  isDark ? "#475569" : "#cbd5e1",
                  isDark ? 0.3 : 0.4
                ),
              },
            }}
          >
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: isDark
                      ? "linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(124, 58, 237, 0.2) 100%)"
                      : "linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(124, 58, 237, 0.15) 100%)",
                    border: `1px solid ${alpha(
                      isDark ? "#8b5cf6" : "#8b5cf6",
                      isDark ? 0.2 : 0.15
                    )}`,
                  }}
                >
                  <Event
                    sx={{
                      color: isDark ? "#a78bfa" : "#8b5cf6",
                      fontSize: 18,
                    }}
                  />
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: isDark ? "#e2e8f0" : "#1e293b",
                    fontSize: "0.95rem",
                  }}
                >
                  Additional Details
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mb: 0.75,
                        fontWeight: 500,
                        color: isDark ? "#cbd5e1" : "#475569",
                        fontSize: "0.8rem",
                      }}
                    >
                      Date of Birth
                    </Typography>
                    {/* Date of Birth Field - DatePicker with custom styling */}
                    <Field name="dob">
                      {({ field, form, meta }) => (
                        <DatePicker
                          value={(field.value)?.toString().startsWith("'") ? field.value.slice(1) : field.value}
                          onChange={(date) =>
                            form.setFieldValue(field.name, date)
                          }
                          onBlur={field.onBlur}
                          disabled={isSaving}
                          enableAccessibleFieldDOMStructure={false}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              error: meta.touched && !!meta.error,
                              helperText: meta.touched && meta.error,
                              placeholder: "DD/MM/YYYY",
                              variant: "outlined",
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 1.5,
                                  backgroundColor: alpha(
                                    isDark ? "#0f172a" : "#ffffff",
                                    isDark ? 0.6 : 0.8
                                  ),
                                  border: `1px solid ${alpha(
                                    isDark ? "#475569" : "#e2e8f0",
                                    isDark ? 0.3 : 0.4
                                  )}`,
                                  "&:hover": {
                                    borderColor: alpha(
                                      isDark ? "#60a5fa" : "#3b82f6",
                                      isDark ? 0.5 : 0.6
                                    ),
                                    backgroundColor: alpha(
                                      isDark ? "#0f172a" : "#ffffff",
                                      isDark ? 0.7 : 0.9
                                    ),
                                  },
                                  "&.Mui-focused": {
                                    borderColor: isDark ? "#60a5fa" : "#3b82f6",
                                    borderWidth: "1.5px",
                                    backgroundColor: alpha(
                                      isDark ? "#0f172a" : "#ffffff",
                                      isDark ? 0.7 : 0.9
                                    ),
                                  },
                                },
                                "& .MuiOutlinedInput-input": {
                                  color: isDark ? "#e2e8f0" : "#1e293b",
                                  fontSize: "0.875rem",
                                  padding: "10px 12px",
                                },
                                "& .MuiFormHelperText-root": {
                                  marginLeft: 0,
                                  fontSize: "0.75rem",
                                },
                              },
                            },
                            field: {
                              clearable: true,
                              format: "dd/MM/yyyy",
                            },
                          }}
                          sx={{
                            width: "100%",
                            "& .MuiIconButton-root": {
                              color: isDark ? "#60a5fa" : "#3b82f6",
                            },
                          }}
                        />
                      )}
                    </Field>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mb: 0.75,
                        fontWeight: 500,
                        color: isDark ? "#cbd5e1" : "#475569",
                        fontSize: "0.8rem",
                      }}
                    >
                      Anniversary Date
                    </Typography>
                    {/* Anniversary Field - DatePicker with custom styling */}
                    <Field name="anniversary">
                      {({ field, form, meta }) => (
                        <DatePicker
                          value={(field.value)?.toString().startsWith("'") ? field.value.slice(1) : field.value}
                          onChange={(date) =>
                            form.setFieldValue(field.name, date)
                          }
                          onBlur={field.onBlur}
                          disabled={isSaving}
                          enableAccessibleFieldDOMStructure={false}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              error: meta.touched && !!meta.error,
                              helperText: meta.touched && meta.error,
                              placeholder: "DD/MM/YYYY",
                              variant: "outlined",
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 1.5,
                                  backgroundColor: alpha(
                                    isDark ? "#0f172a" : "#ffffff",
                                    isDark ? 0.6 : 0.8
                                  ),
                                  border: `1px solid ${alpha(
                                    isDark ? "#475569" : "#e2e8f0",
                                    isDark ? 0.3 : 0.4
                                  )}`,
                                  "&:hover": {
                                    borderColor: alpha(
                                      isDark ? "#60a5fa" : "#3b82f6",
                                      isDark ? 0.5 : 0.6
                                    ),
                                    backgroundColor: alpha(
                                      isDark ? "#0f172a" : "#ffffff",
                                      isDark ? 0.7 : 0.9
                                    ),
                                  },
                                  "&.Mui-focused": {
                                    borderColor: isDark ? "#60a5fa" : "#3b82f6",
                                    borderWidth: "1.5px",
                                    backgroundColor: alpha(
                                      isDark ? "#0f172a" : "#ffffff",
                                      isDark ? 0.7 : 0.9
                                    ),
                                  },
                                },
                                "& .MuiOutlinedInput-input": {
                                  color: isDark ? "#e2e8f0" : "#1e293b",
                                  fontSize: "0.875rem",
                                  padding: "10px 12px",
                                },
                                "& .MuiFormHelperText-root": {
                                  marginLeft: 0,
                                  fontSize: "0.75rem",
                                },
                              },
                            },
                            field: {
                              clearable: true,
                              format: "dd/MM/yyyy",
                            },
                          }}
                          sx={{
                            width: "100%",
                            "& .MuiIconButton-root": {
                              color: isDark ? "#60a5fa" : "#3b82f6",
                            },
                          }}
                        />
                      )}
                    </Field>
                  </Box>
                </Grid>
              </Grid>

              <Typography
                variant="caption"
                sx={{
                  mt: 1.5,
                  display: "block",
                  color: isDark ? "#94A3B8" : "#64748B",
                  fontSize: "0.75rem",
                  fontStyle: "italic",
                }}
              >
                Optional: Used for sending birthday/anniversary greetings
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Footer Actions - Fixed at Bottom */}
        <Box
          sx={{
            pt: 2,
            borderTop: `1px solid ${alpha(isDark ? "#334155" : "#e2e8f0", 0.2)}`,
          }}
        >
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => handleSaveCustomer(formik.values)}
              disabled={isSaving || !formik.isValid}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                textTransform: "none",
                backgroundColor: alpha(
                  isDark ? "#1e293b" : "#ffffff",
                  isDark ? 0.4 : 0.8
                ),
                border: `1.5px solid ${alpha(
                  isDark ? "#475569" : "#cbd5e1",
                  isDark ? 0.3 : 0.4
                )}`,
                color: isDark ? "#e2e8f0" : "#475569",
                py: 1.25,
                fontSize: "0.875rem",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: alpha(
                    isDark ? "#1e293b" : "#ffffff",
                    isDark ? 0.6 : 0.95
                  ),
                  borderColor: alpha(
                    isDark ? "#60a5fa" : "#3b82f6",
                    isDark ? 0.5 : 0.6
                  ),
                  transform: "translateY(-1px)",
                },
                "&.Mui-disabled": {
                  opacity: 0.5,
                  borderColor: alpha(isDark ? "#475569" : "#cbd5e1", 0.2),
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isSaving || !formik.dirty}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                textTransform: "none",
                background: isDark
                  ? "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
                  : "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                boxShadow: isDark
                  ? "0 4px 12px rgba(59, 130, 246, 0.25)"
                  : "0 4px 12px rgba(37, 99, 235, 0.25)",
                color: "#fff",
                py: 1.25,
                fontSize: "0.875rem",
                transition: "all 0.2s ease",
                "&:hover": {
                  background: isDark
                    ? "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)"
                    : "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
                  boxShadow: isDark
                    ? "0 6px 16px rgba(59, 130, 246, 0.35)"
                    : "0 6px 16px rgba(37, 99, 235, 0.35)",
                  transform: "translateY(-1px)",
                },
                "&.Mui-disabled": {
                  background: alpha(isDark ? "#3b82f6" : "#2563eb", 0.3),
                  boxShadow: "none",
                  color: alpha("#fff", 0.5),
                },
              }}
            >
              {isSaving ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                  <span>Saving...</span>
                </Box>
              ) : editingCustomer ? (
                "Update Customer"
              ) : (
                "Save Customer"
              )}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Form>
  );
};

const CustomersPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const {
    customers,
    loading: contextLoading,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    refresh,
  } = useContext(DataContext);

  // AG Grid ref
  const gridRef = useRef();

  // State for local loading states
  const [isPageLoading, setIsPageLoading] = useState(false);
  const isSearching = false;
  const [isDrawerLoading, setIsDrawerLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isGridLoading = false;

  const [searchInput, setSearchInput] = useState("");
  const searchTerm = useDebounce(searchInput, 300);
  const [selectedLetter, setSelectedLetter] = useState("ALL");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [deleteCustomerId, setDeleteCustomerId] = useState(null);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("success");

  const [modalOpen, setModalOpen] = useState(false);
  const [isGridReady, setIsGridReady] = useState(false);

  const onGridReady = (params) => {
    gridRef.current = params;
    setIsGridReady(true);
  };

  // Row selection state
  const [selectedRows, setSelectedRows] = useState([]);

  // Handle row selection
  const handleRowSelect = useCallback((customerId, isSelected) => {
    setSelectedRows((prev) => {
      if (isSelected) {
        return [...prev, customerId];
      } else {
        return prev.filter((id) => id !== customerId);
      }
    });
  }, []);

  // Handle select all
  // const handleSelectAll = useCallback(() => {
  //   const allCustomerIds = customers.map((c) => c.id);
  //   setSelectedRows(allCustomerIds);
  // }, [customers]);

  // // Handle deselect all
  // const handleDeselectAll = useCallback(() => {
  //   setSelectedRows([]);
  // }, []);



  const handleEditCustomer = useCallback((customer) => {
    setEditingCustomer(customer);
    setIsDrawerLoading(true);

    setTimeout(() => {
      setDrawerOpen(true);
      setIsDrawerLoading(false);
    }, 100);
  }, []);

  // =============================================
  // HANDLE INPUT FUNCTION - ADDED HERE
  // =============================================
  const handleInput = useCallback((e, fieldName, formik) => {
    const value = e.target.value;

    // Handle contact fields (main and family members)
    if (fieldName === 'contact' || (fieldName.includes('familyMembers') && fieldName.includes('contact'))) {
      // Allow only numbers, +, -, (, ), spaces
      const filteredValue = value.replace(/[^0-9+\-\s()]/g, '');
      formik.setFieldValue(fieldName, filteredValue);
    }

    // Handle opening balance field
    else if (fieldName === 'openingBalance') {
      // Allow only numbers and one decimal point
      let filteredValue = value.replace(/[^0-9.]/g, '');

      // Ensure only one decimal point
      const parts = filteredValue.split('.');
      if (parts.length > 2) {
        filteredValue = parts[0] + '.' + parts.slice(1).join('');
      }

      formik.setFieldValue(fieldName, filteredValue);
    }

    // For all other fields, allow any input
    else {
      formik.setFieldValue(fieldName, value);
    }
  }, []);

  // Column Definitions for AG Grid
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Sr. No.",
        valueGetter: "node.rowIndex + 1",
        width: 50,
        sortable: false,
        filter: false,
        pinned: "left",
        cellStyle: { textAlign: "center", fontWeight: 600 },
      },
      {
        field: "name",
        headerName: "Name",
        width: 250,
        filter: "agTextColumnFilter",
        sortable: true,
        sort: "asc",
        cellRenderer: NameRenderer,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        field: "contact",
        headerName: "Contact",
        width: 150,
        filter: "agTextColumnFilter",
        sortable: true,
      },
      {
        field: "address",
        headerName: "Address",
        width: 250,
        filter: "agTextColumnFilter",
        sortable: true,
        cellStyle: { whiteSpace: "normal", lineHeight: "1.4" },
        autoHeight: true,
      },
      {
        field: "dob",
        headerName: "DOB",
        width: 120,
        filter: "agDateColumnFilter",
        valueFormatter: (params) => {
          try {
            let dateStr = (params.value || "")?.toString();
            dateStr = dateStr.startsWith("'") ? dateStr.slice(1) : dateStr;
            const date = parse(dateStr, "dd/MM/yyyy", new Date());
            return format(date, "dd MMM");
          } catch {
            return params.value;
          }
        },
        sortable: true,
      },
      {
        field: "anniversary",
        headerName: "Anniversary",
        width: 120,
        filter: "agDateColumnFilter",
        sortable: true,
        valueFormatter: (params) => {
          try {
            let dateStr = (params.value || "")?.toString();
            dateStr = dateStr?.startsWith("'") ? dateStr.slice(1) : dateStr;
            const date = parse(dateStr, "dd/MM/yyyy", new Date());
            return format(date, "dd MMM");
          } catch {
            return params.value;
          }
        },
      },
      {
        headerName: "Family",
        width: 120,
        filter: false,
        sortable: false,
        cellRenderer: FamilyMembersRenderer,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        },
      },
      {
        headerName: "Actions",
        width: 150,
        filter: false,
        sortable: false,
        cellRenderer: ActionsRenderer,
        cellRendererParams: {
          context: {
            handleEditCustomer,
            setDeleteCustomerId,
            setDeleteConfirmOpen,
            setSelectedCustomer,
            setModalOpen,
          },
        },
        pinned: "right",
      },
    ],
    [handleEditCustomer]
  );

  // Default column properties
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 100,
      wrapText: true,
      autoHeight: true,
      cellStyle: {
        borderRight: isDark
          ? "1px solid rgba(255, 255, 255, 0.05)"
          : "1px solid rgba(224, 224, 224, 0.5)",
        display: "flex",
        alignItems: "center",
        whiteSpace: 'normal',
        wordWrap: 'break-word',
      },
    }),
    [isDark]
  );

  // Grid options
  const gridOptions = useMemo(
    () => ({
      rowBuffer: 20,
      cacheBlockSize: 100,
      maxBlocksInCache: 10,
      animateRows: true,
      enableCellTextSelection: true,
      ensureDomOrder: true,
      suppressColumnVirtualisation: false,
      suppressRowVirtualisation: false,
    }),
    []
  );

  // Handle search filtering
  useEffect(() => {
    if (!isGridReady || !gridRef.current?.api) return;

    const timer = setTimeout(() => {
      try {
        if (searchTerm.trim()) {
          const filterModel = {};
          setSelectedLetter("ALL");
          const searchableColumns = ["name"];

          searchableColumns.forEach((column) => {
            filterModel[column] = {
              type: "contains",
              filter: searchTerm,
              filterParams: {
                caseSensitive: false,
              },
            };
          });

          gridRef.current.api.setFilterModel(filterModel);
        } else {
          gridRef.current.api.setFilterModel(null);
          if (selectedLetter !== "ALL") {
            gridRef.current.api.setFilterModel({
              name: {
                type: "startsWith",
                filter: selectedLetter,
              },
            });
          }
        }
      } catch (error) {
        console.error("Error applying search filter:", error);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, isGridReady, selectedLetter]);

  // Memoize available letters
  const availableLetters = useMemo(() => {
    if (!customers || customers.length === 0) return [];
    const letters = new Set();
    customers.forEach((customer) => {
      if (customer.name && customer.name.length > 0) {
        letters.add(customer.name.charAt(0).toUpperCase());
      }
    });
    return Array.from(letters).sort();
  }, [customers]);

  const handleAddCustomer = useCallback(() => {
    setEditingCustomer(null);
    setDrawerOpen(true);
  }, []);

  const showAlert = useCallback((message, type) => {
    setAlertMessage(message);
    setAlertType(type);
    setTimeout(() => setAlertMessage(""), 3000);
  }, []);

  const handleSaveCustomer = useCallback(
    async (values) => {
      // STEP 1: Check for duplicate BEFORE saving
      const duplicate = customers.find(
        c => c.contact?.toString() === values.contact?.toString() &&
          c.id !== editingCustomer?.id
      );

      if (duplicate) {
        showAlert(
          `❌ Phone number "${values.contact}" is already registered with ${duplicate.name}`,
          "error"
        );
        return; // STOP - don't proceed with save
      }

      // STEP 2: If no duplicate, proceed with saving
      setIsSaving(true);
      try {
        const formatDate = (date) => {
          if (!date) return "";
          try {
            return format(date, "dd/MM/yyyy");
          } catch {
            return "";
          }
        };

        const formattedValues = {
          ...values,
          dob: `'${formatDate(values.dob)}`,
          anniversary: `'${formatDate(values.anniversary)}`,
          familyMembers:
            values.familyMembers?.map((member) => ({
              ...member,
              dob: `'${formatDate(member.dob)}`,
              anniversary: `'${formatDate(member.anniversary)}`,
            })) || [],
        };

        let result;

        if (editingCustomer) {
          formattedValues.currentBalance = editingCustomer.currentBalance || 0;
          result = await updateCustomer(editingCustomer.id, formattedValues);

          if (result?.success) {
            showAlert("✅ Customer updated successfully", "success");
            setDrawerOpen(false);
          } else {
            showAlert(result?.message || result?.error || "Failed to update customer", "error");
          }
        } else {
          formattedValues.currentBalance = formattedValues.openingBalance || 0;
          result = await addCustomer(formattedValues);

          if (result?.success) {
            showAlert("✅ Customer added successfully", "success");
            setDrawerOpen(false);
          } else {
            showAlert(result?.message || result?.error || "Failed to add customer", "error");
          }
        }
      } catch (error) {
        console.error("Error saving customer:", error);
        showAlert("An unexpected error occurred", "error");
      } finally {
        setIsSaving(false);
      }
    },
    [editingCustomer, showAlert, addCustomer, updateCustomer, customers] // Added 'customers' to dependencies
  );

  const handleDeleteCustomer = useCallback(
    async (id) => {
      try {
        const result = await deleteCustomer(id);

        if (result?.success) {
          showAlert(
            result.message || "Customer deleted successfully",
            "success"
          );
        } else {
          showAlert(
            result?.message || result?.error || "Failed to delete customer",
            "error"
          );
        }
      } catch (error) {
        console.error("Error deleting customer:", error);
        showAlert("An unexpected error occurred", "error");
      }
      setDeleteConfirmOpen(false);
      setDeleteCustomerId(null);
    },
    [deleteCustomer, showAlert]
  );

  const handleRefresh = useCallback(async () => {
    setIsPageLoading(true);
    try {
      await refresh();
      showAlert("Data refreshed successfully", "success");
    } catch (error) {
      showAlert("Failed to refresh data", "error");
    } finally {
      setTimeout(() => setIsPageLoading(false), 500);
    }
  }, [refresh, showAlert]);

  // Formik initial values
  // In CustomerForm component, update the date fields to use Date objects:
  const getInitialValues = useCallback(() => {
    if (editingCustomer) {
      // Parse string dates to Date objects
      const parseDate = (dateString) => {
        if (!dateString) return null;
        try {
          // Assuming date is stored as DD/MM/YYYY
          dateString = dateString.startsWith("'") ? dateString.slice(1) : dateString;
          const [day, month, year] = dateString.split("/");
          return new Date(year, month - 1, day);
        } catch {
          return null;
        }
      };

      console.log(editingCustomer, "Editing customer details with parsed dates");

      return {
        name: editingCustomer?.name || "",
        emailId: editingCustomer?.emailId || "",
        contact: editingCustomer?.contact || "",
        address: editingCustomer?.address || "",
        dob: parseDate(editingCustomer?.dob),
        anniversary: parseDate(editingCustomer?.anniversary),
        openingBalance: editingCustomer?.openingBalance || "",
        familyMembers:
          editingCustomer?.familyMembers !== "" ? editingCustomer?.familyMembers?.map((member) => ({
            ...member,
            dob: parseDate(member?.dob),
            anniversary: parseDate(member?.anniversary),
          })) || [] : [],
      };
    }
    return initialValues;
  }, [editingCustomer]);

  // Custom loading overlay
  const loadingOverlayComponent = useMemo(() => {
    return function CustomLoadingOverlay() {
      return (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={40} sx={{ color: "#2563EB" }} />
        </Box>
      );
    };
  }, []);

  // Custom no rows overlay
  const noRowsOverlayComponent = useMemo(() => {
    return function CustomNoRowsOverlay() {
      return (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="h6" sx={{ color: isDark ? "#a0a0a0" : "#666" }}>
            {searchTerm
              ? "No customers found matching your search"
              : "No customers found"}
          </Typography>
        </Box>
      );
    };
  }, [isDark, searchTerm]);

  const isLoading = contextLoading || isPageLoading;
  const isFilteringOrSearching = isSearching || isGridLoading;

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress
            size={60}
            thickness={4}
            sx={{ color: "#2563EB", mb: 2 }}
          />
          <Typography
            sx={{
              fontSize: "1.1rem",
              fontWeight: 600,
              color: isDark ? "#e0e0e0" : "#1a1a1a",
            }}
          >
            Loading customers...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          minHeight: "100vh",
          py: 2,
          color: isDark ? "#e0e0e0" : "#1a1a1a",
        }}
      >
        <Backdrop
          open={isDrawerLoading}
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
          <CircularProgress color="primary" />
        </Backdrop>

        <Container maxWidth="xl">
          {/* Alert */}
          {alertMessage && (
            <Alert
              severity={alertType}
              sx={{ mb: 3 }}
              onClose={() => setAlertMessage("")}
            >
              {alertMessage}
            </Alert>
          )}

          {/* Header Section */}
          <Card
            sx={{
              mb: 3,
              background: isDark
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(255, 255, 255, 0.2)",
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ alignItems: "center" }}
              >
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 900,
                      mb: 0.5,
                      color: isDark ? "#e0e0e0" : "#1a1a1a",
                    }}
                  >
                    Customers
                  </Typography>
                  <Typography
                    sx={{
                      color: isDark ? "#a0a0a0" : "#666",
                      fontSize: "1rem",
                    }}
                  >
                    Total Customers:{" "}
                    <Chip
                      label={customers.length}
                      color="primary"
                      size="small"
                    />
                  </Typography>
                </Box>

                <TextField
                  placeholder="Search customers..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          sx={{
                            color: isDark ? "#a0a0a0" : "#666",
                            fontSize: 20,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                  sx={{
                    flex: 1,
                    minWidth: 250,
                    "& .MuiOutlinedInput-root": {
                      color: isDark ? "#e0e0e0" : "#1a1a1a",
                      "& fieldset": {
                        borderColor: isDark
                          ? "rgba(255, 255, 255, 0.2)"
                          : "rgba(0, 0, 0, 0.2)",
                      },
                      "&:hover fieldset": {
                        borderColor: isDark
                          ? "rgba(255, 255, 255, 0.3)"
                          : "rgba(0, 0, 0, 0.3)",
                      },
                    },
                  }}
                />

                <Button
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: "#2563EB",
                    color: "#121826",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      backgroundColor: isDark
                        ? "rgba(37, 99, 235, 0.15)"
                        : "rgba(37, 99, 235, 0.08)",
                    },
                  }}
                >
                  Refresh
                </Button>

                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddCustomer}
                  variant="contained"
                  size="small"
                  sx={{
                    background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Add Customer
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Alphabet Filter */}
          <Card
            sx={{
              mb: 3,
              background: isDark
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(255, 255, 255, 0.2)",
              border: isDark
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(0, 0, 0, 0.08)",
            }}
          >
            <CardContent sx={{ p: 1.5 }}>
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ flexWrap: "wrap", alignItems: "center" }}
              >
                <Button
                  onClick={() => setSelectedLetter("ALL")}
                  size="small"
                  variant={selectedLetter === "ALL" ? "contained" : "outlined"}
                  sx={{
                    background:
                      selectedLetter === "ALL"
                        ? "linear-gradient(135deg, #2563EB, #1D4ED8)"
                        : "transparent",
                    color: selectedLetter === "ALL" ? "#fff" : "#000",
                    borderColor: "#2563EB",
                    minWidth: 36,
                    height: 36,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    padding: "4px 8px",
                    border:
                      selectedLetter === "ALL"
                        ? "none"
                        : "1px solid rgb(26, 29, 35)",
                  }}
                >
                  ALL
                </Button>
                {availableLetters.map((letter) => (
                  <Button
                    key={letter}
                    onClick={() => setSelectedLetter(letter)}
                    size="small"
                    variant={
                      selectedLetter === letter ? "contained" : "outlined"
                    }
                    sx={{
                      background:
                        selectedLetter === letter
                          ? "linear-gradient(135deg, #2563EB, #1D4ED8)"
                          : "transparent",
                      color: selectedLetter === letter ? "#fff" : "#000",
                      borderColor: "#2563EB",
                      minWidth: 36,
                      height: 36,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      padding: "4px 6px",
                      border:
                        selectedLetter === letter
                          ? "none"
                          : "1px solid rgb(26, 29, 35)",
                      borderRadius: "6px",
                    }}
                  >
                    {letter}
                  </Button>
                ))}
              </Stack>
            </CardContent>
          </Card>

          {/* AG Grid Table */}
          <Card
            elevation={0}
            sx={{
              borderRadius: "20px",
              overflow: "hidden",
              position: "relative",
              height: "calc(100vh - 400px)",
              minHeight: "500px",
              background: isDark
                ? "linear-gradient(180deg, #1a1a2e 0%, #16161a 100%)"
                : "linear-gradient(180deg, #ffffff 0%,rgba(248, 250, 252, 0.26) 100%)",
              border: isDark
                ? "1px solid rgba(255, 255, 255, 0.06)"
                : "1px solid rgba(0, 0, 0, 0.04)",
              boxShadow: isDark
                ? "0 4px 24px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05) inset"
                : "0 4px 24px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(255, 255, 255, 0.8) inset",
            }}
          >
            {isFilteringOrSearching && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  zIndex: 10,
                  background:
                    "linear-gradient(90deg, transparent, #2563eb, #8b5cf6, #2563eb, transparent)",
                  backgroundSize: "200% 100%",
                  animation: "shimmer 1.5s infinite linear",
                  "@keyframes shimmer": {
                    "0%": { backgroundPosition: "100% 0" },
                    "100%": { backgroundPosition: "-100% 0" },
                  },
                }}
              />
            )}

            <CardContent sx={{ p: 0, height: "100%" }}>
              <Box
                className={isDark ? "ag-theme-alpine-dark" : "ag-theme-alpine"}
                sx={{
                  width: "100%",
                  height: "100%",
                  "& .ag-root-wrapper": {
                    border: "none !important",
                    borderRadius: "20px",
                  },
                  "& .ag-header": {
                    background: isDark
                      ? "linear-gradient(180deg, #252536 0%, #1e1e2d 100%) !important"
                      : "linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 100%) !important",
                    borderBottom: isDark
                      ? "1px solid rgba(255, 255, 255, 0.08) !important"
                      : "1px solid rgba(0, 0, 0, 0.06) !important",
                  },
                  "& .ag-header-cell": {
                    fontWeight: "700 !important",
                    fontSize: "12px !important",
                    textTransform: "uppercase !important",
                    letterSpacing: "0.8px !important",
                    color: isDark ? "#94a3b8 !important" : "#64748b !important",
                    borderRight: "none !important",
                    transition: "background 0.2s ease !important",
                    "&:hover": {
                      background: isDark
                        ? "rgba(37, 99, 235, 0.1) !important"
                        : "rgba(37, 99, 235, 0.05) !important",
                    },
                  },
                  "& .ag-body-viewport": {
                    background: isDark
                      ? "#16161a !important"
                      : "#ffffff !important",
                  },
                  "& .ag-row": {
                    borderBottom: isDark
                      ? "1px solid rgba(255, 255, 255, 0.04) !important"
                      : "1px solid rgba(0, 0, 0, 0.03) !important",
                    transition: "all 0.2s ease !important",
                    "&:hover": {
                      background: isDark
                        ? "linear-gradient(90deg, rgba(37, 99, 235, 0.08), rgba(139, 92, 246, 0.05)) !important"
                        : "linear-gradient(90deg, rgba(37, 99, 235, 0.04), rgba(139, 92, 246, 0.02)) !important",
                      transform: "scale(1.001)",
                    },
                  },
                  "& .ag-row-odd": {
                    background: isDark
                      ? "rgba(255, 255, 255, 0.01) !important"
                      : "rgba(248, 250, 252, 0.5) !important",
                  },
                  "& .ag-row-even": {
                    background: isDark
                      ? "transparent !important"
                      : "#ffffff !important",
                  },
                  "& .ag-cell": {
                    display: "flex !important",
                    alignItems: "center !important",
                    color: isDark ? "#e2e8f0 !important" : "#1e293b !important",
                    fontSize: "14px !important",
                    fontWeight: "500 !important",
                    borderRight: "none !important",
                    padding: "0 16px !important",
                  },
                }}
              >
                <AgGridReact
                  ref={gridRef}
                  columnDefs={columnDefs}
                  rowData={customers}
                  defaultColDef={defaultColDef}
                  gridOptions={gridOptions}
                  loadingOverlayComponent={loadingOverlayComponent}
                  noRowsOverlayComponent={noRowsOverlayComponent}
                  rowHeight={68}
                  headerHeight={52}
                  animateRows={true}
                  enableCellTextSelection={true}
                  ensureDomOrder={true}
                  suppressContextMenu={true}
                  suppressMenuHide={false}
                  suppressMovableColumns={true}
                  context={{
                    selectedRows,
                    handleRowSelect,
                  }}
                  onGridReady={(params) => {
                    params.api.sizeColumnsToFit();
                    onGridReady(params);
                    window.addEventListener("resize", () => {
                      setTimeout(() => params.api.sizeColumnsToFit());
                    });
                  }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Customer Action Modal */}
          <CustomerActionModal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            customer={selectedCustomer}
            isDarkMode={isDark}
          />

          {/* Add/Edit Customer Drawer with Formik */}
          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={() => !isSaving && setDrawerOpen(false)}
            sx={{
              zIndex: (theme) => theme.zIndex.drawer + 2,
              "& .MuiDrawer-paper": {
                width: { xs: "100%", sm: 500 },
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                background: isDark
                  ? "linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)"
                  : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)",
                borderLeft: `1px solid ${alpha(
                  isDark ? theme.palette.divider : "#3b82f6",
                  isDark ? 0.1 : 0.15
                )}`,
                boxShadow: isDark
                  ? "0 0 60px rgba(0, 0, 0, 0.4)"
                  : "0 0 60px rgba(59, 130, 246, 0.15)",
              },
            }}
          >
            <Formik
              initialValues={getInitialValues()}
              validationSchema={CustomerSchema}
              enableReinitialize
              onSubmit={(values) => handleSaveCustomer(values)}
            >
              {(formik) => (
                <CustomerForm
                  formik={formik}
                  isDark={isDark}
                  isSaving={isSaving}
                  editingCustomer={editingCustomer}
                  handleSaveCustomer={() => formik.handleSubmit()}
                  handleInput={handleInput} // Pass handleInput to CustomerForm
                  customers={customers}
                />
              )}
            </Formik>
          </Drawer>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteConfirmOpen}
            onClose={() => setDeleteConfirmOpen(false)}
          >
            <DialogTitle>Delete Customer</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete this customer? This action
                cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setDeleteConfirmOpen(false)}
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDeleteCustomer(deleteCustomerId)}
                variant="contained"
                color="error"
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </LocalizationProvider>
  );
};

export default React.memo(CustomersPage);